import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { IImpact } from "@/pages/dashboard/utils/types";
import { ExportResponse, filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import { Packaging } from "./PackagingOverview";

export interface PackagingImpact {
  impact: IImpact;
}

export interface PackagingProductRelations {
  id: number;
  external_id: string;
  name: string;
  relation: "proxy" | "primary_source";
  unrelated: boolean;
}

export interface PackagingProductRelationsResponse {
  total_count: number;
  relations: PackagingProductRelations[];
}

export interface PackagingOrderRelations {
  year: number;
  po: number;
  so: number;
  total: number;
}

export interface PackagingOrderRelationsResponse {
  relations: PackagingOrderRelations[];
  total: number;
}

export interface IPackagingBom {
  id: number;
  name: string;
  supplier_name: string;
  supplier_address: string;
  quantity: number;
  unit: string;
  mass: number;
  impact: IImpact;
  status: "complete" | "incomplete";
}

export interface GetPackagingBom {
  bom: IPackagingBom[];
  total_count: number;
}

export function useGetPackagings({
  year,
  pagination,
  filter,
  search,
  sort,
  enabled,
  showUnrelated = false,
}: {
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
  showUnrelated?: boolean;
}) {
  const yearParam = year ? { year } : {};
  const showUnrelatedParam = showUnrelated ? { show_unrelated: showUnrelated } : {};

  return useQuery({
    queryKey: ["packagings", "list", year, showUnrelated, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<Packaging>>("/packagings", {
          params: {
            ...yearParam,
            ...showUnrelatedParam,
            page: pagination?.page === undefined ? 1 : pagination.page,
            ...(pagination?.pageSize ? { page_size: pagination?.pageSize } : {}),
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export interface SearchPackaging {
  id: number;
  label: string;
}

export function useGetPackagingsSearch({
  search,
  selectedIds,
}: {
  search?: string;
  selectedIds?: number[];
}) {
  const searchParam = search ? { search } : {};
  const selectedIdsParam = selectedIds ? { selected_ids: selectedIds.join(";") } : {};

  return useInfiniteQuery({
    queryKey: ["packagings", "search", search || "", selectedIds?.sort().join(",") || ""],
    queryFn: ({ pageParam = 1 }) =>
      axios.get<GetData<SearchPackaging>>("/packagings/search", {
        params: {
          ...searchParam,
          ...selectedIdsParam,
          page: pageParam,
          page_size: 50,
        },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.data.total_count || 0;
      const itemsLoaded = allPages.reduce((sum, page) => sum + page.data.data.length, 0);
      const hasMore = itemsLoaded < totalItems;
      return hasMore ? allPages.length + 1 : undefined;
    },
    keepPreviousData: true,
  });
}

export function useGetPackagingsPerYear() {
  return useQuery({
    queryKey: ["packagings", "list", "years"],
    keepPreviousData: true,
    queryFn: () => axios.get<YearStatus[]>("/packagings/years", {}).then((result) => result.data),
  });
}

export function useGetPackaging({ packagingId }: { packagingId: number }) {
  return useQuery({
    queryKey: ["packagings", "detail", packagingId],
    keepPreviousData: true,
    queryFn: () =>
      axios.get<Packaging>(`/packagings/${packagingId}`, {}).then((result) => result.data),
  });
}

// export function usePackagingImpact({ packagingId }: { packagingId: number }) {
//   return useQuery({
//     queryKey: ["packagings", "detail", packagingId, "impact"],
//     keepPreviousData: true,
//     queryFn: () =>
//       axios
//         .get<PackagingImpact>(`/packagings/${packagingId}/impact`, {})
//         .then((result) => result.data),
//   });
// }

export function usePackagingProductRelations({
  packagingId,
  pagination,
}: {
  packagingId: number;
  pagination: Pagination;
}) {
  return useQuery({
    queryKey: ["packagings", "detail", packagingId, "product_relations", pagination],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<PackagingProductRelationsResponse>(`/packagings/${packagingId}/product_relations`, {
          params: { page: pagination.page, page_size: pagination.pageSize },
        })
        .then((result) => result.data),
  });
}

export function usePackagingOrderRelations({ packagingId }: { packagingId: number }) {
  return useQuery({
    queryKey: ["packagings", "detail", packagingId, "order_relations"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<PackagingOrderRelationsResponse>(`/packagings/${packagingId}/order_relations`, {})
        .then((result) => result.data),
  });
}

export function usePackagingBoms({ packagingId }: { packagingId: number }) {
  return useQuery({
    queryKey: ["packagings", "detail", packagingId, "bom"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetPackagingBom>(`/packagings/${packagingId}/bom`, {})
        .then((result) => result.data),
  });
}

export function useDeletePackagingBom() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ packagingId }: { packagingId: number }) =>
      axios.delete("/packagings/" + packagingId + "/bom"),
    onSuccess: (_, { packagingId }) => {
      smartQueryClient.invalidateQueries(["packagings", "detail", packagingId]);
    },
  });
}

export function useExportPackagingBom() {
  return useMutation({
    mutationKey: ["packagingBomExport"],
    mutationFn: ({ packagingId }: { packagingId: number }) =>
      axios
        .get<ExportResponse>(`/packagings/${packagingId}/bom/export`, {})
        .then((result) => result.data),
  });
}

export function useDeletePackaging() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ packagingIds }: { packagingIds: number[] }) =>
      axios.delete("/packagings", {
        params: {
          ids: packagingIds.join(";"),
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["packagings"]);
    },
  });
}
